import type { SVGProps } from 'react';
import { brands, type Brand } from '../theme/tokens';
import { brandAccent } from '../theme/themes';
import { useBrandOptional, useResolvedBrand } from '../theme/BrandProvider';

type MarkSpec = {
  text: string;
  /** viewBox width. Height is fixed at 24 so `height` scales predictably. */
  width: number;
  letterSpacing: number;
  /** Glyph left of the wordmark. Shape differs per brand so the cue survives greyscale. */
  glyph: 'bar' | 'ring';
};

const MARKS: Record<Brand, MarkSpec> = {
  talt: { text: 'TALT', width: 92, letterSpacing: 5, glyph: 'bar' },
  royalaura: { text: 'ROYAL AURA', width: 176, letterSpacing: 3, glyph: 'ring' },
};

export type BrandWordmarkProps = Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> & {
  brand?: Brand;
  height?: number;
  /** `brand` paints the accent; `inherit` uses `currentColor` for reversed surfaces. */
  tone?: 'brand' | 'inherit';
};

export function BrandWordmark({ brand, height = 24, tone = 'brand', ...rest }: BrandWordmarkProps) {
  const resolved = useResolvedBrand(brand, 'BrandWordmark');
  const context = useBrandOptional();
  const definition = brands[resolved];
  const mark = MARKS[resolved];
  // The light-theme accent is unreadable on a dark surface, so follow the resolved mode.
  const fill =
    tone === 'brand' ? brandAccent(resolved, context?.resolvedMode ?? 'light') : 'currentColor';
  const titleId = `wordmark-${resolved}`;

  return (
    <svg
      role="img"
      aria-labelledby={titleId}
      data-brand={resolved}
      viewBox={`0 0 ${mark.width} 24`}
      height={height}
      width={(mark.width / 24) * height}
      fill={fill}
      {...rest}
    >
      <title id={titleId}>{definition.name}</title>
      {mark.glyph === 'bar' ? (
        <rect x="0" y="4" width="4" height="16" rx="1" />
      ) : (
        <circle cx="9" cy="12" r="7" fill="none" stroke={fill} strokeWidth="3" />
      )}
      <text
        x={mark.glyph === 'bar' ? 14 : 24}
        y="17"
        fontFamily="Segoe UI, system-ui, sans-serif"
        fontSize="16"
        fontWeight="600"
        letterSpacing={mark.letterSpacing}
      >
        {mark.text}
      </text>
    </svg>
  );
}
